/*
 * The Inherent Risk Questionnaire (IRQ), shared by TPRM and UCRM.
 */
import { AssessmentSelections } from './DueDiligence';
import { FileUpdates, UploadedFile } from './Files';
import { RiskRating } from './RiskRating';

/**
 */
export interface RiskRatingPointThresholds {
    low: number;
    low_moderate: number;
    moderate: number;
    moderate_high: number;
}

export interface InherentRiskQuestionnaireSection {
    is_applicable: boolean;
    name: string;
    questions: IrqQuestion[];
    files: UploadedFile[];
    text?: string;
}

export interface InherentRiskQuestionnaireSectionConfiguration {
    name: string;
    questions: IrqQuestion[];
}

/**
 * A TPRM or UCRM inherent risk questionnaire.
 * @param third_party_id The ID of the organization (TPRM third party or UCRM business). The TPRM-specific name is fixed by existing TPRM data, unless we decide to do a migration.
 * @param service_id The ID of the assessment subject (TPRM third-party service or UCRM use case). The TPRM-specific name is fixed by existing TPRM data, unless we decide to do a migration.
 */
export interface InherentRiskQuestionnaireResponse {
    third_party_id: string;
    service_id: string;
    has_been_submitted: boolean;
    thresholds: RiskRatingPointThresholds;
    sections: InherentRiskQuestionnaireSection[];

    comments?: string;
    inherent_risk_score?: 1 | 2 | 3 | 4 | 5;
}

export interface InherentRiskQuestionnaireSectionSelections {
    is_applicable: boolean;
    file_updates?: FileUpdates;
    answers: IrqAnswer[];
    text?: string;
}

export interface InherentRiskQuestionnaireConfiguration {
    thresholds: RiskRatingPointThresholds;
    sections: InherentRiskQuestionnaireSectionConfiguration[];
}

export enum IrqQuestionType {
    DATE = 'Date',
    FILE_UPLOAD = 'File upload',
    FREEFORM_TEXT = 'Freeform text',
    MULTIPLE_SELECT = 'Multiple-select',
    SINGLE_SELECT = 'Single-select',
}

export interface IrqAnswerOption {
    text: string;
    points: number;
}

export interface IrqQuestionDate {
    _type: IrqQuestionType.DATE;
    answer?: string;
    text: string;
}

export interface IrqQuestionFileUpload {
    _type: IrqQuestionType.FILE_UPLOAD;
    answer: UploadedFile[];
    text: string;
}

export interface IrqQuestionFreeformText {
    _type: IrqQuestionType.FREEFORM_TEXT;
    answer?: string;
    text: string;
}

export interface IrqQuestionMultipleSelect {
    _type: IrqQuestionType.MULTIPLE_SELECT;
    answer: number[];
    options: IrqAnswerOption[];
    text: string;
}

export interface IrqQuestionSingleSelect {
    _type: IrqQuestionType.SINGLE_SELECT;
    answer?: number;
    options: IrqAnswerOption[];
    text: string;
}

export type IrqQuestion = IrqQuestionDate | IrqQuestionFileUpload | IrqQuestionFreeformText | IrqQuestionMultipleSelect | IrqQuestionSingleSelect;

export interface IrqAnswerDate {
    _type: IrqQuestionType.DATE;
    value?: string;
}

export interface IrqAnswerFileUpload {
    _type: IrqQuestionType.FILE_UPLOAD;
    value: FileUpdates;
}

export interface IrqAnswerFreeformText {
    _type: IrqQuestionType.FREEFORM_TEXT;
    value?: string;
}

export interface IrqAnswerMultipleSelect {
    _type: IrqQuestionType.MULTIPLE_SELECT;
    value: number[];
}

export interface IrqAnswerSingleSelect {
    _type: IrqQuestionType.SINGLE_SELECT;
    value?: number;
}

export type IrqAnswer = IrqAnswerDate | IrqAnswerFileUpload | IrqAnswerFreeformText | IrqAnswerMultipleSelect | IrqAnswerSingleSelect;

/** The members shared by `TprmSubmitInherentRiskQuestionnaireSelections` and `UcrmSubmitInherentRiskQuestionnaireSelections`. */
export interface SubmitInherentRiskQuestionnaireSelectionsBase {
    assessment_selections: AssessmentSelections[]; // The list of assessments to generate for the assessment subject.
}

/** The members shared by `TprmSaveOrSubmitInherentRiskQuestionnaireRequest` and `UcrmSaveOrSubmitInherentRiskQuestionnaireRequest`. Extending interfaces narrow `submit_selections` to their module's type. */
export interface SaveOrSubmitInherentRiskQuestionnaireRequestBase {
    section_selections: InherentRiskQuestionnaireSectionSelections[];
    submit: boolean;
    submit_selections: SubmitInherentRiskQuestionnaireSelectionsBase; // Ignored if `submit` is false.

    comments?: string;
    inherent_risk_score?: RiskRating;
}

export const irqQuestionIsAnswered = (question: IrqQuestion): boolean => {
    switch (question._type) {
        case IrqQuestionType.DATE:
        case IrqQuestionType.FREEFORM_TEXT:
        case IrqQuestionType.SINGLE_SELECT:
            return question.answer !== undefined && question.answer !== '';
        case IrqQuestionType.FILE_UPLOAD:
        case IrqQuestionType.MULTIPLE_SELECT:
            return question.answer !== undefined && question.answer.length > 0;
    }
};
